import React, { useState } from 'react';
import { FileText } from 'lucide-react';
import FileDropZone from '../shared/FileDropZone';
import DocumentTypeSelector from '../shared/DocumentTypeSelector';
import IntakeReviewPanel from './IntakeReviewPanel';
import { callIntakeAgent } from '../../utils/callIntakeAgent';
import { normalizeDocumentType } from '../../utils/documentTypes';
import { useAuth } from '../../hooks/useAuth';

/**
 * Onda 9A abr/26 — IntakeZone.
 * Componente universal de upload/texto → extract → review → apply.
 * Substitui os IntakeSection locais: ficha do imóvel, empresa, contato, oferta.
 *
 * Props:
 *  - entityType: 'imovel'|'empresa'|'contato'|'oferta'|null (genérico)
 *  - entityId: uuid da entidade (ou null)
 *  - defaultDocType: tipo pré-selecionado no dropdown (string vazia = auto-detectar)
 *  - onComplete: callback após apply bem-sucedido
 *  - title: label custom na section (default 'Intake de documento')
 */
export default function IntakeZone({ entityType, entityId, defaultDocType = '', onComplete, title = 'Intake de documento' }) {
  const { user } = useAuth();
  const [fileData, setFileData] = useState(null);
  const [text, setText] = useState('');
  const [docType, setDocType] = useState(defaultDocType);
  const [runId, setRunId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleExtract = async () => {
    if ((!fileData && !text.trim()) || loading) return;
    setLoading(true);
    setError(null);
    try {
      const payload = {
        mode: 'extract',
        initiated_by: user?.email,
        source_type: fileData ? 'upload' : 'pasted_text',
      };
      // ADR-013: vocabulário canônico em todo write.
      const normalizedType = normalizeDocumentType(docType);
      if (normalizedType) payload.document_type = normalizedType;
      if (entityType && entityId) {
        payload.context = { entity_type: entityType, entity_id: entityId };
        if (entityType === 'imovel') payload.imovel_id = entityId;
        if (entityType === 'empresa') payload.empresa_id = entityId;
        if (entityType === 'contato') payload.contato_id = entityId;
        if (entityType === 'oferta') payload.oferta_id = entityId;
      }
      if (fileData) {
        payload.file_base64 = fileData.base64;
        payload.mime_type = fileData.mime_type;
        payload.file_name = fileData.name;
      } else {
        payload.text = text.trim();
      }
      const result = await callIntakeAgent(payload);
      if (result?.ok) {
        setRunId(result.run_id);
        setFileData(null);
        setText('');
      } else {
        setError(result?.error || 'Falha ao processar documento');
      }
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  if (runId) {
    return (
      <IntakeReviewPanel
        intakeRunId={runId}
        onComplete={() => { setRunId(null); if (onComplete) onComplete(); }}
        onCancel={() => setRunId(null)}
      />
    );
  }

  return (
    <section>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2 flex items-center gap-1">
        <FileText size={10} /> {title}
      </div>

      <DocumentTypeSelector value={docType} onChange={setDocType} className="mb-2" />

      <FileDropZone
        onFile={setFileData}
        label="Arraste PDF, imagem ou documento"
        disabled={loading}
      />

      {!fileData && (
        <details className="mt-2">
          <summary className="text-[10px] text-slate-400 cursor-pointer hover:text-slate-600">
            Ou cole texto manualmente
          </summary>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Cole o texto do documento aqui..."
            rows={3}
            className="w-full text-xs border border-slate-200 rounded p-2 resize-y mt-1 focus:outline-none focus:border-blue-400"
          />
        </details>
      )}

      <div className="mt-2 flex items-center gap-2">
        <button
          onClick={handleExtract}
          disabled={(!text.trim() && !fileData) || loading}
          className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Processando...' : fileData ? `Processar ${fileData.name}` : 'Extrair e analisar'}
        </button>
        {error && (
          <span className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 truncate" title={error}>
            {error}
          </span>
        )}
      </div>
    </section>
  );
}
